/**
 * Cena 3D gerada da rede importada: terreno, trechos, água andando, equipamentos por tipo e casas agrupadas
 * — Nexora Grid (frontend, US-405; nasceu na prova S3).
 *
 * Criado em: 16/10/2026 · Última modificação: 25/10/2026 · Versão: 3.2.0
 *
 * Nada aqui é desenhado à mão: tudo sai do cadastro (DWG importado). Nível de detalhe semântico:
 * - visão geral: cada equipamento é um símbolo instanciado do seu tipo e as casas são instanciadas;
 * - aproximando: os equipamentos mais próximos ganham o GLB detalhado do tipo, com prioridade para VRP,
 *   bombas e macromedidores sobre reservatórios (v2: as VRPs sumiam atrás de outros equipamentos).
 * v2 (S3 v2): água andando pela simulação (Agua.tsx), cor dos trechos por setor ou por pressão (NBR 12218) na
 * hora da simulação, setor selecionado em destaque, nível real no reservatório detalhado, rótulos agrupados
 * quando vários equipamentos estão juntos (RAP-001 a RAP-005) e duplo clique no terreno para voar até o ponto.
 * v2.1: bombas, poços e elevatórias usam a ESTAÇÃO PADRÃO das fotos de campo (abrigo, quadro de comando, cavalete
 * com macromedidor e sensor de pressão, poço ou bomba de superfície); as sinaleiras do quadro acendem pelo
 * estado da bomba (simulação).
 * v3 (retorno de 17/10/2026): dados AO VIVO (nível, bombas); estações agrupadas (elevatória + bombas, poço + bomba) sem
 * peças soltas; água sempre visível na altura real; quadros ao lado dos equipamentos com gráfico da última hora.
 * v3.1 (retorno de 18/10/2026): bomba submersa — a estação mostra a boca do poço no cavalete e, abaixo dela, o corte do
 * poço com a bomba no crivo e os níveis da ficha (`pocoSubmerso.ts`); a bomba de superfície some quando todas são
 * submersas. Os quadros ficam AO LADO do contorno do desenho na tela, nunca em cima dele (`Quadro3d`).
 */
import { Html, Line, useGLTF } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { arquivoScada, ESTACAO_COMANDO } from "../modelosSaneamento";
import { Agua, type Tempo } from "./Agua";
import { montarCorte, PROFUNDIDADE_DESENHO } from "./pocoSubmerso";
import { Quadro3d, type Leitor } from "./Quadro3d";
import { agruparCasas, projetar, type Equipamento, type Rede } from "./rede";
import type { Relevo } from "./relevo";
import { interpolar, NBR_MAX, NBR_MIN, type Simulacao } from "./simulacao";
import { geometriaTerreno, segmentosTubulacao, raioNaDistancia, type SegmentoTubo } from "./malhaRede";
import { nivelReservatorio } from "./nivelReservatorio";
import { montarVisualReservatorio } from "./reservatorioVisual";

export const EXAGERO = 3; // exagero vertical do relevo
export const PILOTO = "RSE";
const RAIO_DETALHE = 600;
const MAX_DETALHADOS = 14;
const RAIO_GRUPO_ROTULO = 180;
const RAIO_ESTACAO_M = 15;     // equipamentos de estação mais próximos que isso são a mesma estação (elevatória + bombas)
const RAIO_ABSORVE_M = 12;     // registro/macromedidor/ventosa colados na estação fazem parte do cavalete
const MAX_QUADROS = 4;       // o do selecionado + até 3 outros
const DISTANCIA_ENTRE_QUADROS_M = 60;
const TIPOS_ESTACAO = new Set(["bomba", "poco_profundo", "elevatoria"]);

const ARQUIVO_ESTACAO = ESTACAO_COMANDO;
const ARQUIVO_POR_TIPO: Record<string, string> = {
  bomba: ARQUIVO_ESTACAO,
  elevatoria: ARQUIVO_ESTACAO,
  poco_profundo: ARQUIVO_ESTACAO,
  macromedidor: arquivoScada("macromedidor", "")!,
  vrp: arquivoScada("vrp", "")!,
  ponto_pressao: arquivoScada("ponto_pressao", "")!,
};
/** Peso na disputa pelo modelo detalhado (maior = ganha de mais longe). */
const PESO: Record<string, number> = { vrp: 4, bomba: 2.5, poco_profundo: 2.5, elevatoria: 2.5, macromedidor: 2, reservatorio: 1.5 };

/** Símbolo da visão geral por tipo (forma, cor e tamanho em metros). */
const SIMBOLO: Record<string, { geometria: () => THREE.BufferGeometry; cor: string; tamanho: number }> = {
  reservatorio: { geometria: () => new THREE.CylinderGeometry(1, 1, 1.2, 24), cor: "#c8f3ff", tamanho: 32 },
  bomba: { geometria: () => new THREE.BoxGeometry(1.6, 1, 1), cor: "#c084fc", tamanho: 16 },
  elevatoria: { geometria: () => new THREE.BoxGeometry(1.6, 1.2, 1.2), cor: "#c084fc", tamanho: 24 },
  poco_profundo: { geometria: () => new THREE.ConeGeometry(0.8, 1.6, 6), cor: "#a78bfa", tamanho: 16 },
  macromedidor: { geometria: () => new THREE.SphereGeometry(0.8, 16, 12), cor: "#22d3ee", tamanho: 14 },
  vrp: { geometria: () => new THREE.OctahedronGeometry(1), cor: "#facc15", tamanho: 24 },
  registro: { geometria: () => new THREE.OctahedronGeometry(1), cor: "#9ca3af", tamanho: 4 },
  registro_fechado: { geometria: () => new THREE.OctahedronGeometry(1), cor: "#ef4444", tamanho: 6 },
  ventosa: { geometria: () => new THREE.TetrahedronGeometry(1), cor: "#94a3b8", tamanho: 5 },
  hidrante: { geometria: () => new THREE.TetrahedronGeometry(1), cor: "#fb7185", tamanho: 6 },
  ponto_pressao: { geometria: () => new THREE.SphereGeometry(0.8, 16, 12), cor: "#38bdf8", tamanho: 12 },
};

const PALETA = ["#40bee6", "#a78bfa", "#f472b6", "#34d399", "#fbbf24", "#60a5fa", "#fb923c", "#22d3ee", "#c084fc",
  "#4ade80", "#f87171", "#e879f9"];
const APAGADO = "#16323f";
/** Faixas de pressão (as mesmas da S1): < 10 crítico, 10–25, 25–40, > 40 acima da estática máxima. */
export const FAIXAS_PRESSAO = [
  { ate: NBR_MIN, cor: "#e5383b", rotulo: `< ${NBR_MIN} mca — abaixo do mínimo` },
  { ate: 25, cor: "#4f8fe6", rotulo: `${NBR_MIN}–25 mca` },
  { ate: NBR_MAX, cor: "#3fbf7f", rotulo: `25–${NBR_MAX} mca` },
  { ate: Infinity, cor: "#f28c28", rotulo: `> ${NBR_MAX} mca — acima do máximo` },
];

export type ModoCor = "setor" | "pressao";

export function corDoSetor(setor: string, setores: string[]): string {
  if (setor.startsWith(PILOTO)) return "#7fe9ff";
  return PALETA[(setores.indexOf(setor) + 1) % PALETA.length];
}

export function corDaPressao(p: number | undefined): string {
  if (p === undefined) return APAGADO;
  return (FAIXAS_PRESSAO.find((f) => p < f.ate) ?? FAIXAS_PRESSAO[3]).cor;
}

export function alturaCena(relevo: Relevo, rede: Rede, x: number, z: number): number {
  return (relevo.cota(x, z) - rede.cotaMin) * EXAGERO;
}

function Terreno({ rede, relevo, aoFocar }: { rede: Rede; relevo: Relevo; aoFocar: (p: THREE.Vector3) => void }) {
  const geometria = useMemo(() => geometriaTerreno(rede.extensao, (x, z) => alturaCena(relevo, rede, x, z)), [rede, relevo]);
  return (
    <group>
      <mesh name="terreno-sombreado" geometry={geometria.terreno} onDoubleClick={(e) => { e.stopPropagation(); aoFocar(e.point.clone()); }}>
        <meshStandardMaterial vertexColors roughness={0.95} metalness={0.05} />
      </mesh>
      <lineSegments name="curvas-nivel" geometry={geometria.contornos} raycast={() => null}>
        <lineBasicMaterial color="#738f87" transparent opacity={0.23} depthWrite={false} />
      </lineSegments>
      <lineSegments name="grade-terreno" geometry={geometria.referencia} raycast={() => null}>
        <lineBasicMaterial color="#5a747c" transparent opacity={0.12} depthWrite={false} />
      </lineSegments>
    </group>
  );
}

function Trechos({ rede, relevo, setores, destaquePiloto, setorSelecionado, modo, pressoes, tubulacao }: {
  rede: Rede; relevo: Relevo; setores: string[]; destaquePiloto: boolean; setorSelecionado: string | null;
  modo: ModoCor; pressoes: Map<string, number> | null; tubulacao: boolean;
}) {
  const segmentos = useMemo(() => segmentosTubulacao(rede, (x, z) => alturaCena(relevo, rede, x, z)), [rede, relevo]);
  const camera = useThree((s) => s.camera);
  const tubos = useRef<THREE.InstancedMesh>(null);
  const unioes = useRef<THREE.InstancedMesh>(null);
  const juntas = useMemo(() => {
    const mapa = new Map<string, { ponto: THREE.Vector3; segmento: SegmentoTubo }>();
    for (const segmento of segmentos) for (const ponto of [segmento.de, segmento.para]) {
      const chave = ponto.toArray().map((v) => v.toFixed(4)).join("|");
      const atual = mapa.get(chave);
      if (!atual || atual.segmento.raio < segmento.raio) mapa.set(chave, { ponto, segmento });
    }
    return [...mapa.values()];
  }, [segmentos]);
  const geometria = useMemo(() => {
    const posicoes: number[] = [];
    for (const s of segmentos) posicoes.push(...s.de.toArray(), ...s.para.toArray());
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(posicoes, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(new Float32Array(posicoes.length), 3));
    return g;
  }, [segmentos]);
  useLayoutEffect(() => {
    const cores = geometria.getAttribute("color") as THREE.BufferAttribute;
    const cor = new THREE.Color(), a = new THREE.Color(), b = new THREE.Color();
    const corEm = (s: SegmentoTubo, t: number) => {
      const trecho = s.trecho;
      const apagado = (setorSelecionado && trecho.setor !== setorSelecionado) || (destaquePiloto && !trecho.setor.startsWith(PILOTO));
      if (apagado) return cor.set(APAGADO);
      if (modo === "pressao") return cor.copy(a.set(corDaPressao(pressoes?.get(trecho.de))))
        .lerp(b.set(corDaPressao(pressoes?.get(trecho.para))), t);
      return cor.set(corDoSetor(trecho.setor, setores));
    };
    const obj = new THREE.Object3D(), direcao = new THREE.Vector3(), eixo = new THREE.Vector3(0, 1, 0);
    let k = 0;
    segmentos.forEach((s, i) => {
      for (const t of [s.inicio, s.fim]) {
        corEm(s, t);
        cores.setXYZ(k++, cor.r, cor.g, cor.b);
      }
      if (tubos.current) {
        direcao.subVectors(s.para, s.de);
        obj.position.copy(s.de).add(s.para).multiplyScalar(0.5);
        obj.quaternion.setFromUnitVectors(eixo, direcao.clone().normalize());
        const raio = raioNaDistancia(s.raio, s.trecho.dn, camera.position.distanceTo(obj.position));
        obj.scale.set(raio, direcao.length(), raio); obj.updateMatrix();
        tubos.current.setMatrixAt(i, obj.matrix);
        tubos.current.setColorAt(i, corEm(s, (s.inicio + s.fim) / 2));
      }
    });
    juntas.forEach(({ ponto, segmento }, i) => {
      if (!unioes.current) return;
      obj.position.copy(ponto); obj.quaternion.identity();
      obj.scale.setScalar(raioNaDistancia(segmento.raio, segmento.trecho.dn, camera.position.distanceTo(ponto)) * 1.06); obj.updateMatrix();
      unioes.current.setMatrixAt(i, obj.matrix);
      unioes.current.setColorAt(i, corEm(segmento, segmento.inicio));
    });
    for (const mesh of [tubos.current, unioes.current]) if (mesh) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
    cores.needsUpdate = true;
  }, [geometria, segmentos, juntas, setores, destaquePiloto, setorSelecionado, modo, pressoes, tubulacao]);
  const ultimaCamera = useRef(new THREE.Vector3(Infinity, Infinity, Infinity));
  const auxiliares = useMemo(() => ({ obj: new THREE.Object3D(), eixo: new THREE.Vector3(0, 1, 0), direcao: new THREE.Vector3() }), []);
  useFrame(() => {
    if (!tubulacao || !tubos.current || !unioes.current || camera.position.distanceToSquared(ultimaCamera.current) < 0.01) return;
    ultimaCamera.current.copy(camera.position);
    const { obj, eixo, direcao } = auxiliares;
    segmentos.forEach((s, i) => {
      obj.position.copy(s.de).add(s.para).multiplyScalar(0.5);
      direcao.subVectors(s.para, s.de);
      const comprimento = direcao.length();
      obj.quaternion.setFromUnitVectors(eixo, direcao.normalize());
      const r = raioNaDistancia(s.raio, s.trecho.dn, camera.position.distanceTo(obj.position));
      obj.scale.set(r, comprimento, r); obj.updateMatrix(); tubos.current!.setMatrixAt(i, obj.matrix);
    });
    juntas.forEach(({ ponto, segmento }, i) => {
      obj.position.copy(ponto); obj.quaternion.identity();
      obj.scale.setScalar(raioNaDistancia(segmento.raio, segmento.trecho.dn, camera.position.distanceTo(ponto)) * 1.06);
      obj.updateMatrix(); unioes.current!.setMatrixAt(i, obj.matrix);
    });
    tubos.current.instanceMatrix.needsUpdate = true;
    unioes.current.instanceMatrix.needsUpdate = true;
  });
  if (tubulacao) return <group name="rede-tubulacao">
    <instancedMesh name="tubos-rede" ref={tubos} args={[undefined, undefined, segmentos.length]} raycast={() => null}>
      <cylinderGeometry args={[1, 1, 1, 10]} />
      <meshStandardMaterial metalness={0.45} roughness={0.38} />
    </instancedMesh>
    <instancedMesh name="juncoes-rede" ref={unioes} args={[undefined, undefined, juntas.length]} raycast={() => null}>
      <sphereGeometry args={[1, 10, 6]} />
      <meshStandardMaterial metalness={0.5} roughness={0.35} />
    </instancedMesh>
  </group>;
  return (
    <lineSegments name="rede-linhas" geometry={geometria} raycast={() => null}>
      <lineBasicMaterial vertexColors toneMapped={false} />
    </lineSegments>
  );
}

type Item = { equipamento: Equipamento; posicao: THREE.Vector3 };

/** Símbolos instanciados por tipo; o símbolo some quando o equipamento está detalhado. */
function Simbolos({ itens, detalhados, setorSelecionado, aoSelecionar }: {
  itens: Item[]; detalhados: Set<Item>; setorSelecionado: string | null; aoSelecionar: (e: Equipamento) => void;
}) {
  const porTipo = useMemo(() => {
    const grupos = new Map<string, Item[]>();
    itens.forEach((i) => grupos.set(i.equipamento.tipo, [...(grupos.get(i.equipamento.tipo) ?? []), i]));
    return [...grupos.entries()].filter(([tipo]) => SIMBOLO[tipo]);
  }, [itens]);
  return (
    <>
      {porTipo.map(([tipo, lista]) => (
        <SimbolosDoTipo key={tipo} tipo={tipo} lista={lista} detalhados={detalhados} setorSelecionado={setorSelecionado}
          aoSelecionar={aoSelecionar} />
      ))}
    </>
  );
}

function SimbolosDoTipo({ tipo, lista, detalhados, setorSelecionado, aoSelecionar }: {
  tipo: string; lista: Item[]; detalhados: Set<Item>; setorSelecionado: string | null; aoSelecionar: (e: Equipamento) => void;
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const simbolo = SIMBOLO[tipo];
  const geometria = useMemo(() => simbolo.geometria(), [simbolo]);
  const camera = useThree((s) => s.camera);
  const aux = useMemo(() => new THREE.Object3D(), []);
  const relogio = useRef(1);
  useFrame((_, delta) => {
    relogio.current += delta;
    if (relogio.current < 0.15 || !ref.current) return;
    relogio.current = 0;
    lista.forEach((item, i) => {
      const volume = Number(item.equipamento.detalhe.match(/\d+/)?.[0] ?? 0);
      const base = tipo === "reservatorio" ? 18 + Math.cbrt(volume || 100) * 2.2 : simbolo.tamanho;
      const fora = setorSelecionado && item.equipamento.setor !== setorSelecionado;
      const s = base * (0.02 + 0.98 * THREE.MathUtils.smoothstep(item.posicao.distanceTo(camera.position), 60, 900)) * (fora ? 0.5 : 1);
      aux.position.copy(item.posicao).add(new THREE.Vector3(0, s * 0.6 + 2, 0));
      aux.scale.setScalar(detalhados.has(item) ? 0.0001 : s); // `detalhados` = detalhados + partes de uma estação
      aux.updateMatrix();
      ref.current!.setMatrixAt(i, aux.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });
  return (
    <instancedMesh ref={ref} args={[geometria, undefined, lista.length]}
      onClick={(e) => { e.stopPropagation(); if (e.instanceId !== undefined) aoSelecionar(lista[e.instanceId].equipamento); }}>
      <meshStandardMaterial color={simbolo.cor} emissive={simbolo.cor} emissiveIntensity={0.35} transparent opacity={0.85} />
    </instancedMesh>
  );
}

/** Escala ilustrativa pelo volume explícito, limitada para manter os tipos legíveis na rede geográfica. */
function escalaReservatorio(e: Equipamento): number {
  const volume = Number(e.detalhe.match(/(\d+(?:[.,]\d+)?)\s*m[³3]/i)?.[1]?.replace(",", ".") ?? 100);
  return THREE.MathUtils.clamp(Math.cbrt(volume / 100), 0.7, 2) * (/REL[-_ ]?\d/i.test(e.id) ? 0.7 : 1);
}

/** Fator de crescimento com a distância (pequenos crescem mais para continuarem visíveis). */
function fatorDistancia(tipo: string, distancia: number): number {
  return tipo === "reservatorio" ? THREE.MathUtils.clamp(distancia / 55, 1.5, 16) : THREE.MathUtils.clamp(distancia / 22, 1.5, 24);
}

const SINAIS = ["sinal_ligada", "sinal_desligada", "sinal_falha"] as const;
type Estacao = { membros: Item[]; comPoco: boolean; comSuperficie: boolean; submersa: Equipamento | null };
const ehSubmersa = (e: Equipamento) => e.tipo === "poco_profundo" || Boolean(e.submersa);

/** GLB detalhado: reservatório com a água na altura real (ao vivo); estação montada pelos membros, sinaleiras ao vivo. */
function Detalhado({ item, estacao, nivelPct, nivelMetros, ligada, aoSelecionar, registrar }: {
  item: Item; estacao?: Estacao; nivelPct: number | null; nivelMetros: number | null; ligada: boolean | null; aoSelecionar: (e: Equipamento) => void;
  registrar: (item: Item, modelo: THREE.Object3D | null) => void;
}) {
  const e = item.equipamento;
  const arquivo = arquivoScada(e.tipo, e.id)!;
  const { scene } = useGLTF(arquivo);
  const camera = useThree((s) => s.camera);
  const modelo = useMemo(() => {
    const copia = scene.clone(true);
    copia.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      obj.material = Array.isArray(obj.material) ? obj.material.map((m) => m.clone()) : obj.material.clone();
      obj.castShadow = true;
      obj.receiveShadow = true;
    });
    return copia;
  }, [scene]);
  const agua = useRef<{ no: THREE.Object3D; y0: number; minY: number } | null>(null);
  const visualNivel = useRef<ReturnType<typeof montarVisualReservatorio> | null>(null);
  const nivel = nivelReservatorio(e, nivelMetros, nivelPct);
  const sinais = useRef<Partial<Record<(typeof SINAIS)[number], THREE.MeshStandardMaterial>>>({});
  const corte = useRef<THREE.Group | null>(null);
  const escalaReal = e.tipo === "reservatorio" ? escalaReservatorio(e) : 1;
  useLayoutEffect(() => {
    modelo.position.copy(item.posicao);
    modelo.scale.setScalar(1);
    modelo.userData.arquivoScada = arquivo;
    if (arquivo === ARQUIVO_ESTACAO) {
      // estação = membros próximos: poço se houver poço; bomba de superfície (dentro da casa) se houver bomba/elevatória
      const poco = modelo.getObjectByName("poco");
      const superficie = modelo.getObjectByName("bomba_superficie");
      if (poco) poco.visible = estacao ? estacao.comPoco : ehSubmersa(e);
      if (superficie) superficie.visible = estacao ? estacao.comSuperficie : !ehSubmersa(e);
      // bomba submersa: corte do poço abaixo da boca (círculo do cavalete), com os níveis da ficha
      const submersa = estacao ? estacao.submersa : ehSubmersa(e) ? e : null;
      if (corte.current) { corte.current.parent?.remove(corte.current); corte.current = null; }
      if (poco && submersa) {
        const escala = modelo.scale.x;
        modelo.scale.setScalar(1);
        modelo.updateMatrixWorld(true);
        const caixa = new THREE.Box3().setFromObject(poco);
        modelo.scale.setScalar(escala);
        const centro = caixa.getCenter(new THREE.Vector3()).sub(modelo.position);
        corte.current = montarCorte(submersa.poco);
        corte.current.position.set(centro.x, caixa.min.y - modelo.position.y, centro.z);
        modelo.add(corte.current);
      }
    }
    const no = modelo.getObjectByName("agua") as THREE.Mesh | undefined;
    if (no?.geometry) {
      // a água fica fora do efeito holograma: sempre visível, azul translúcido
      no.userData.semHolograma = true;
      no.material = new THREE.MeshStandardMaterial({ color: "#1e88e5", emissive: "#0d47a1", emissiveIntensity: 0.35,
        transparent: true, opacity: 0.85, roughness: 0.2 });
      no.geometry.computeBoundingBox();
      agua.current = { no, y0: no.position.y, minY: no.geometry.boundingBox!.min.y };
    }
    if (e.tipo === "reservatorio") {
      visualNivel.current = montarVisualReservatorio(modelo, arquivo.includes("reservatorio_elevado"));
    }
    for (const nome of SINAIS) {
      const malha = modelo.getObjectByName(nome) as THREE.Mesh | undefined;
      if (malha) sinais.current[nome] = malha.material as THREE.MeshStandardMaterial;
    }
    registrar(item, modelo);
    return () => {
      registrar(item, null);
      visualNivel.current?.destruir();
      visualNivel.current = null;
    };
  }, [modelo, item, estacao]); // eslint-disable-line react-hooks/exhaustive-deps
  useFrame(() => {
    const distancia = camera.position.distanceTo(item.posicao);
    modelo.scale.setScalar(escalaReal * fatorDistancia(e.tipo, distancia));
    // o corte do poço só aparece de perto (atravessa o terreno)
    if (corte.current) corte.current.visible = distancia < 25 * PROFUNDIDADE_DESENHO;
    visualNivel.current?.atualizar(nivel, distancia);
    if (ligada !== null) {
      const acender = (nome: (typeof SINAIS)[number], aceso: boolean) => {
        const m = sinais.current[nome];
        if (!m) return;
        m.emissive.copy(m.color);
        m.emissiveIntensity = aceso ? 3 : 0.02;
      };
      acender("sinal_ligada", ligada);
      acender("sinal_desligada", !ligada);
      acender("sinal_falha", false);
    }
    if (agua.current && nivelPct !== null) {
      // altura real: o nível da telemetria; o fundo da água fica parado (base no fundo, qualquer origem do modelo)
      const s = Math.max(0.03, Math.min(1, nivelPct / 100));
      agua.current.no.scale.y = s;
      agua.current.no.position.y = agua.current.y0 + agua.current.minY * (1 - s);
    }
  });
  return <primitive object={modelo} onClick={(ev: { stopPropagation: () => void }) => { ev.stopPropagation(); aoSelecionar(e); }} />;
}

/** Casas instanciadas a partir das peças do GLB da casa (uma chamada de desenho por peça). */
function Casas({ lista, rotulos }: { lista: { posicao: THREE.Vector3; casas: number }[]; rotulos: boolean }) {
  const { scene } = useGLTF("/modelos/casa_v1.glb");
  const pecas = useMemo(() => {
    const resultado: { geometria: THREE.BufferGeometry; material: THREE.Material }[] = [];
    scene.updateMatrixWorld(true);
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) resultado.push({ geometria: m.geometry.clone().applyMatrix4(m.matrixWorld), material: m.material as THREE.Material });
    });
    return resultado;
  }, [scene]);
  return (
    <>
      {pecas.map((p, k) => <InstanciasCasa key={k} geometria={p.geometria} material={p.material} lista={lista} />)}
      {rotulos && lista.map((c, i) => (
        <Html key={i} position={[c.posicao.x, c.posicao.y + 12, c.posicao.z]} center className="selo-casas" zIndexRange={[50, 0]}>{c.casas}</Html>
      ))}
    </>
  );
}

function InstanciasCasa({ geometria, material, lista }: {
  geometria: THREE.BufferGeometry; material: THREE.Material; lista: { posicao: THREE.Vector3 }[];
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const camera = useThree((s) => s.camera);
  const aux = useMemo(() => new THREE.Object3D(), []);
  const relogio = useRef(1);
  // símbolo do grupo de casas: grande de longe, do tamanho de uma casa de perto
  useFrame((_, delta) => {
    relogio.current += delta;
    if (relogio.current < 0.2 || !ref.current) return;
    relogio.current = 0;
    lista.forEach((c, i) => {
      aux.position.copy(c.posicao);
      aux.scale.setScalar(22 * THREE.MathUtils.clamp(c.posicao.distanceTo(camera.position) / 900, 0.12, 1));
      aux.updateMatrix();
      ref.current!.setMatrixAt(i, aux.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });
  return <instancedMesh ref={ref} args={[geometria, material, lista.length]} raycast={() => null} />;
}

/** Grupos de equipamentos próximos (para um rótulo só enquanto a câmera está longe). */
function agrupar(itens: Item[]): Item[][] {
  const grupos: Item[][] = [];
  for (const item of itens) {
    const grupo = grupos.find((g) => g[0].posicao.distanceTo(item.posicao) < RAIO_GRUPO_ROTULO);
    if (grupo) grupo.push(item); else grupos.push([item]);
  }
  return grupos;
}

type Rotulo = { chave: string; texto: string; item?: Item; centro?: THREE.Vector3; grupo: number; ordem: number };
type EstadoRotulo = { visivel: boolean; y: number };

export type PropsCena = {
  rede: Rede; relevo: Relevo; setores: string[]; destaquePiloto: boolean; rotulos: boolean;
  setorSelecionado: string | null; modo: ModoCor; sim: Simulacao | null; horaInteira: number; tubulacao?: boolean;
  tempo: React.MutableRefObject<Tempo>;
  /** valor ao vivo de um dispositivo (canal de tempo real) */
  ler: Leitor; empresa: string | null; quadros: boolean; focoId?: string | null;
  aoSelecionar: (e: Equipamento) => void; aoFocar: (p: THREE.Vector3) => void;
};

/** Estações: equipamentos de estação a menos de 15 m são uma estação só (o primeiro elevatória/bomba representa). */
function agruparEstacoes(itens: Item[]): { estacoes: Map<Item, Estacao>; absorvidos: Set<Item> } {
  const estacoes = new Map<Item, Estacao>();
  const absorvidos = new Set<Item>();
  const daEstacao = itens.filter((i) => TIPOS_ESTACAO.has(i.equipamento.tipo))
    .sort((a, b) => Number(b.equipamento.tipo === "elevatoria") - Number(a.equipamento.tipo === "elevatoria"));
  for (const item of daEstacao) {
    const rep = [...estacoes.keys()].find((r) => r.posicao.distanceTo(item.posicao) < RAIO_ESTACAO_M);
    if (rep) { estacoes.get(rep)!.membros.push(item); absorvidos.add(item); continue; }
    estacoes.set(item, { membros: [item], comPoco: false, comSuperficie: false, submersa: null });
  }
  estacoes.forEach((est) => {
    // submersa (poço profundo ou bomba com instalação submersa na ficha): boca do poço no cavalete e bomba no poço
    est.submersa = est.membros.map((m) => m.equipamento).find(ehSubmersa) ?? null;
    est.comPoco = Boolean(est.submersa);
    est.comSuperficie = est.membros.some((m) => !ehSubmersa(m.equipamento));
  });
  const reps = [...estacoes.keys()];
  for (const item of itens) {
    if (["registro", "registro_fechado", "macromedidor", "ventosa", "hidrante"].includes(item.equipamento.tipo)
      && reps.some((r) => r.posicao.distanceTo(item.posicao) < RAIO_ABSORVE_M)) absorvidos.add(item);
  }
  return { estacoes, absorvidos };
}

export function Cena({ rede, relevo, setores, destaquePiloto, rotulos, setorSelecionado, modo, sim, horaInteira, tempo, tubulacao = true,
  ler, empresa, quadros, focoId, aoSelecionar, aoFocar }: PropsCena) {
  const camera = useThree((s) => s.camera);
  // diagnóstico de desempenho: ?sem=terreno,modelos,casas,trechos,agua
  const sem = useMemo(() => new Set((new URLSearchParams(location.search).get("sem") ?? "").split(",")), []);
  const posicao = (x: number, z: number) => new THREE.Vector3(x, alturaCena(relevo, rede, x, z), z);
  const itens = useMemo<Item[]>(() => rede.equipamentos.map((e) => ({ equipamento: e, posicao: posicao(e.x, e.z) })),
    [rede, relevo]); // eslint-disable-line react-hooks/exhaustive-deps
  const casas = useMemo(() => agruparCasas(rede).map((c) => ({ posicao: posicao(c.x, c.z), casas: c.casas })),
    [rede, relevo]); // eslint-disable-line react-hooks/exhaustive-deps
  const { estacoes, absorvidos } = useMemo(() => agruparEstacoes(itens), [itens]);
  const grupos = useMemo(() => agrupar(itens.filter((i) => !absorvidos.has(i) &&
    ["reservatorio", "elevatoria", "vrp", "macromedidor", "poco_profundo", "bomba", "ponto_pressao"].includes(i.equipamento.tipo))), [itens, absorvidos]);
  const altura = useCallback((x: number, z: number) => alturaCena(relevo, rede, x, z), [relevo, rede]);
  const corTrecho = useCallback((setor: string) => corDoSetor(setor, setores), [setores]);
  const [detalhados, setDetalhados] = useState<Set<Item>>(new Set());
  // modelo detalhado de cada item: o quadro se posiciona ao lado do contorno dele na tela
  const modelos = useRef(new Map<Item, THREE.Object3D>());
  const registrar = useCallback((item: Item, modelo: THREE.Object3D | null) => {
    if (modelo) modelos.current.set(item, modelo); else modelos.current.delete(item);
  }, []);
  const candidatos = useMemo<Rotulo[]>(() => grupos.flatMap((g, k) => [
    ...(g.length > 1 ? [{ chave: `g-${g[0].equipamento.id}-${k}`, texto: `${g[0].equipamento.id} +${g.length - 1}`,
      centro: g[0].posicao, grupo: k, ordem: -1 }] : []),
    ...g.map((i, ordem) => ({ chave: `${i.equipamento.id}-${i.posicao.x}`, item: i, grupo: k, ordem,
      texto: `${i.equipamento.id || i.equipamento.tipo}${i.equipamento.detalhe ? ` · ${i.equipamento.detalhe}` : ""}` })),
  ]), [grupos]);
  const [estadoRotulos, setEstadoRotulos] = useState<Record<string, EstadoRotulo>>({});
  const relogio = useRef(1);
  useFrame((_, delta) => {
    relogio.current += delta;
    if (relogio.current < 0.35) return; // nível de detalhe e rótulos ~3 vezes por segundo
    relogio.current = 0;
    const perto = itens
      .filter((i) => !absorvidos.has(i) && (ARQUIVO_POR_TIPO[i.equipamento.tipo] || i.equipamento.tipo === "reservatorio")
        && i.posicao.distanceTo(camera.position) < RAIO_DETALHE * Math.min(2, PESO[i.equipamento.tipo] ?? 1))
      .sort((a, b) => Number(b.equipamento.id === focoId) - Number(a.equipamento.id === focoId)
        || a.posicao.distanceTo(camera.position) / (PESO[a.equipamento.tipo] ?? 1)
        - b.posicao.distanceTo(camera.position) / (PESO[b.equipamento.tipo] ?? 1))
      .slice(0, MAX_DETALHADOS);
    if (perto.length !== detalhados.size || perto.some((i) => !detalhados.has(i))) setDetalhados(new Set(perto));
    const novo: Record<string, EstadoRotulo> = {};
    for (const r of candidatos) {
      const g = grupos[r.grupo];
      const longe = g.length > 1 && g[0].posicao.distanceTo(camera.position) > 450;
      if (!r.item) {
        novo[r.chave] = { visivel: rotulos && longe, y: g[0].posicao.y + 40 };
        continue;
      }
      const e = r.item.equipamento;
      const d = r.item.posicao.distanceTo(camera.position);
      const modelo = modelos.current.get(r.item);
      const topo = modelo ? new THREE.Box3().setFromObject(modelo).max.y - r.item.posicao.y
        : e.tipo === "reservatorio" ? 12 * escalaReservatorio(e) * fatorDistancia(e.tipo, d) : 2.5 * fatorDistancia(e.tipo, d);
      // no mesmo grupo, rótulos escalonados para não se sobreporem
      novo[r.chave] = { visivel: rotulos && !longe, y: r.item.posicao.y + topo + 4 + r.ordem * Math.max(4, d * 0.075) };
    }
    const mudou = candidatos.some((r) => {
      const a = estadoRotulos[r.chave], b = novo[r.chave];
      return !a || a.visivel !== b.visivel || Math.abs(a.y - b.y) > 1;
    });
    if (mudou) setEstadoRotulos(novo);
  });
  const pressoes = sim ? sim.pressoes[horaInteira % sim.horas] : null;
  const ocultos = useMemo(() => new Set([...detalhados, ...absorvidos]), [detalhados, absorvidos]);
  // quadros sem sobreposição: o do equipamento em foco primeiro; depois os mais próximos, afastados entre si
  const quadrosVisiveis: Item[] = [];
  for (const item of [...detalhados].sort((a, b) => Number(b.equipamento.id === focoId) - Number(a.equipamento.id === focoId)
    || a.posicao.distanceTo(camera.position) - b.posicao.distanceTo(camera.position))) {
    if (quadrosVisiveis.length >= MAX_QUADROS) break;
    if (quadrosVisiveis.some((q) => q.posicao.distanceTo(item.posicao) < DISTANCIA_ENTRE_QUADROS_M)) continue;
    quadrosVisiveis.push(item);
  }
  const ligadaDe = (membros: Item[]): boolean | null => {
    const estados = membros.map((m) => ler(m.equipamento.deviceId, "bomba_ligada") ?? ((ler(m.equipamento.deviceId, "potencia_ativa_kw") ?? -1) >= 0
      ? (ler(m.equipamento.deviceId, "potencia_ativa_kw") ?? 0) > 0.5 : null)).filter((v): v is boolean => v !== null);
    return estados.length ? estados.some(Boolean) : null;
  };
  return (
    <group>
      {!sem.has("terreno") && <Terreno rede={rede} relevo={relevo} aoFocar={aoFocar} />}
      {rede.divisas?.features.map((f, i) => <Line key={`divisa-${i}`} points={f.geometry.coordinates.map(([lon, lat]) => {
        const p = projetar(rede, lon, lat); return [p.x, alturaCena(relevo, rede, p.x, p.z) + 1.2, p.z] as [number, number, number];
      })} color="#fbbf24" lineWidth={2} dashed dashSize={8} gapSize={5} />)}
      {!sem.has("trechos") && <Trechos rede={rede} relevo={relevo} setores={setores} destaquePiloto={destaquePiloto}
        setorSelecionado={setorSelecionado} modo={modo} pressoes={pressoes} tubulacao={tubulacao} />}
      {!sem.has("agua") && sim && <Agua rede={rede} sim={sim} tempo={tempo} altura={altura} corDoTrecho={corTrecho} tubulacao={tubulacao} />}
      {!sem.has("modelos") && <Simbolos itens={itens} detalhados={ocultos} setorSelecionado={setorSelecionado}
        aoSelecionar={aoSelecionar} />}
      {!sem.has("modelos") && [...detalhados].map((item) => (
        // cada modelo carrega no seu Suspense: um GLB novo não esconde (nem desmonta) o resto da cena
        <Suspense key={`${item.equipamento.id}-${item.posicao.x}`} fallback={null}>
          <Detalhado item={item} estacao={estacoes.get(item)} aoSelecionar={aoSelecionar} registrar={registrar}
            nivelPct={item.equipamento.tipo === "reservatorio" ? ler(item.equipamento.deviceId, "nivel_reservatorio_pct") : null}
            nivelMetros={item.equipamento.tipo === "reservatorio" ? ler(item.equipamento.deviceId, "nivel_m") : null}
            ligada={TIPOS_ESTACAO.has(item.equipamento.tipo) ? ligadaDe(estacoes.get(item)?.membros ?? [item]) : null} />
        </Suspense>
      ))}
      {!sem.has("casas") && <Suspense fallback={null}><Casas lista={casas} rotulos={rotulos} /></Suspense>}
      {quadros && quadrosVisiveis.map((item, ordem) => {
        const est = estacoes.get(item);
        const d = item.posicao.distanceTo(camera.position);
        const altura = item.equipamento.tipo === "reservatorio" ? 5 * escalaReservatorio(item.equipamento) * fatorDistancia("reservatorio", d)
          : 1.6 * fatorDistancia(item.equipamento.tipo, d);
        return <Quadro3d key={`q-${item.equipamento.id}-${item.posicao.x}`} posicao={item.posicao} altura={altura}
          alvo={() => modelos.current.get(item) ?? null} prioritario={ordem === 0}
          principal={item.equipamento} membros={est ? est.membros.map((m) => m.equipamento) : []} empresa={empresa} ler={ler}
          rotulo={est && est.membros.length > 1 ? `Estação ${item.equipamento.id} · ${est.membros.length} equipamentos` : undefined} />;
      })}
      {candidatos.map((r) => {
        const estado = estadoRotulos[r.chave];
        const base = r.item?.posicao ?? r.centro!;
        return (
          <Html key={r.chave} center className="etiqueta" zIndexRange={[50, 0]} position={[base.x, estado?.y ?? base.y, base.z]}
            style={{ display: estado?.visivel ? "block" : "none" }}>
            <button type="button" className={r.centro ? "grupo" : undefined}
              onClick={() => (r.item ? aoSelecionar(r.item.equipamento) : r.centro && aoFocar(r.centro))}>{r.texto}</button>
          </Html>
        );
      })}
    </group>
  );
}

[...new Set(Object.values(ARQUIVO_POR_TIPO)), arquivoScada("reservatorio", "REL-001")!, arquivoScada("reservatorio", "RAP-001")!,
  "/modelos/casa_v1.glb"].forEach((a) => useGLTF.preload(a));
